import React, { useEffect, useState } from "react";
import { Mail, MessageSquare, Send, Download } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";

const WEBHOOK_KEYS = ["registration", "competition_start", "category_unlock", "suspicious", "completion", "winner"];
const TPL_KEYS = ["registration", "reminder", "completion", "announcement"];

export default function Comms() {
  const [settings, setSettings] = useState(null);
  const [emailLogs, setEmailLogs] = useState({ logs: [], counts: {} });
  const [discordLogs, setDiscordLogs] = useState({ logs: [] });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: el }, { data: dl }] = await Promise.all([
        api.get("/admin/settings"), api.get("/admin/emails/logs"), api.get("/admin/discord/logs"),
      ]);
      setSettings(s);
      setEmailLogs(el);
      setDiscordLogs(dl);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try {
      await api.put("/admin/settings", patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendTestEmail = async () => {
    if (!testTo) { toast.error("Enter a recipient email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/test", { to: testTo });
      if (data.status === "sent") toast.success("Test email sent!");
      else toast.error(`Not sent: ${data.error}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const broadcast = async () => {
    if (!window.confirm("Send a competition reminder email to ALL registered participants?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/broadcast-reminder", { kind: "reminder" });
      toast.success(`Sent ${data.sent}, failed ${data.failed}, pending ${data.pending}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const testDiscord = async (key) => {
    try {
      const { data } = await api.post("/admin/discord/test", { webhook_key: key });
      if (data.status === "sent") toast.success("Discord test message sent!");
      else toast.error(`Not sent: ${data.error}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!settings) return <div className="text-slate-400 font-semibold py-20 text-center">Loading…</div>;

  return (
    <div data-testid="admin-comms">
      <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Emails & Discord</h1>
      <p className="text-sm text-slate-500 mt-1">SMTP, templates, webhooks and delivery logs.</p>

      <Tabs defaultValue="email" className="mt-6">
        <TabsList>
          <TabsTrigger value="email" data-testid="tab-email"><Mail className="w-4 h-4 mr-2" /> Email</TabsTrigger>
          <TabsTrigger value="discord" data-testid="tab-discord"><MessageSquare className="w-4 h-4 mr-2" /> Discord</TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <div className="grid lg:grid-cols-2 gap-6 mt-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
              <h2 className="font-heading font-bold text-slate-900">SMTP Configuration</h2>
              <p className="text-xs text-slate-500">For Gmail: host <code>smtp.gmail.com</code>, port <code>587</code>, username = your Gmail, password = <b>App Password</b> (Google Account → Security → App Passwords).</p>
              {[["host", "SMTP Host"], ["port", "Port"], ["username", "Username / Gmail"], ["password", "App Password"], ["from_email", "From Email"], ["from_name", "From Name"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{l}</label>
                  <Input data-testid={`smtp-${k}`} type={k === "password" ? "password" : "text"} className="mt-1.5" value={settings.smtp[k] ?? ""}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, [k]: k === "port" ? Number(e.target.value) : e.target.value } })} />
                </div>
              ))}
              <button onClick={() => save({ smtp: settings.smtp })} data-testid="smtp-save" className="w-full py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Save SMTP</button>
              <div className="flex gap-2 pt-2">
                <Input data-testid="test-email-to" placeholder="test@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
                <button onClick={sendTestEmail} disabled={busy} data-testid="send-test-email" className="px-4 py-2 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors shrink-0 inline-flex items-center gap-2"><Send className="w-4 h-4" /> Test</button>
              </div>
              <button onClick={broadcast} disabled={busy} data-testid="broadcast-reminder" className="w-full py-3 rounded-lg border-2 border-[#138808] text-[#138808] font-bold hover:bg-[#138808] hover:text-white transition-colors">Broadcast Reminder to All Participants</button>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="font-heading font-bold text-slate-900 mb-4">Delivery Status</h2>
                <div className="grid grid-cols-3 gap-3 text-center">
                  {[["Sent", emailLogs.counts?.sent || 0, "#138808"], ["Failed", emailLogs.counts?.failed || 0, "#DC2626"], ["Pending", emailLogs.counts?.pending || 0, "#E6892E"]].map(([l, v, c]) => (
                    <div key={l} className="rounded-xl bg-slate-50 border border-slate-100 p-4"><div className="font-timer text-2xl font-bold" style={{ color: c }}>{v}</div><div className="text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400">{l}</div></div>
                  ))}
                </div>
                <a href={`${API}/admin/export/email_logs`} data-testid="export-email-logs" className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-[#FF9933]"><Download className="w-3.5 h-3.5" /> Export email logs CSV</a>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="font-heading font-bold text-slate-900 mb-3">Recent Email Log</h2>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {emailLogs.logs.map((l) => (
                    <div key={l.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                      <span className="truncate text-slate-600"><b>{l.type}</b> → {l.to}</span>
                      <span className={`font-bold shrink-0 ml-2 ${l.status === "sent" ? "text-[#138808]" : l.status === "failed" ? "text-red-600" : "text-amber-600"}`}>{l.status}</span>
                    </div>
                  ))}
                  {emailLogs.logs.length === 0 && <p className="text-sm text-slate-400">No emails logged yet.</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-heading font-bold text-slate-900 mb-1">Email Templates</h2>
            <p className="text-xs text-slate-400 mb-4">Placeholders: {"{{name}} {{display_name}} {{competition}} {{org}} {{date}} {{time}} {{categories}} {{questions}} {{link}} {{discord}} {{support}} {{score}} {{correct}} {{subject}} {{message}}"}</p>
            <div className="grid lg:grid-cols-2 gap-5">
              {TPL_KEYS.map((k) => (
                <div key={k} className="border border-slate-200 rounded-xl p-4">
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">{k} template</div>
                  <Input data-testid={`tpl-${k}-subject`} value={settings.email_templates?.[k]?.subject || ""} className="mb-2 text-sm"
                    onChange={(e) => setSettings({ ...settings, email_templates: { ...settings.email_templates, [k]: { ...settings.email_templates[k], subject: e.target.value } } })} />
                  <Textarea data-testid={`tpl-${k}-body`} rows={5} className="text-xs font-mono" value={settings.email_templates?.[k]?.body || ""}
                    onChange={(e) => setSettings({ ...settings, email_templates: { ...settings.email_templates, [k]: { ...settings.email_templates[k], body: e.target.value } } })} />
                </div>
              ))}
            </div>
            <button onClick={() => save({ email_templates: settings.email_templates })} data-testid="tpl-save" className="mt-5 px-6 py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Save Templates</button>
          </div>
        </TabsContent>

        <TabsContent value="discord">
          <div className="grid lg:grid-cols-2 gap-6 mt-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
              <h2 className="font-heading font-bold text-slate-900">Webhook URLs</h2>
              <p className="text-xs text-slate-500">Create webhooks in Discord: Server Settings → Integrations → Webhooks → Copy URL. Each event type can be enabled/disabled independently.</p>
              {WEBHOOK_KEYS.map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{k.replace(/_/g, " ")}</label>
                    <Input data-testid={`webhook-${k}`} className="mt-1 text-xs font-mono" placeholder="https://discord.com/api/webhooks/…" value={settings.discord?.webhooks?.[k] || ""}
                      onChange={(e) => setSettings({ ...settings, discord: { ...settings.discord, webhooks: { ...settings.discord.webhooks, [k]: e.target.value } } })} />
                  </div>
                  <div className="flex flex-col items-center gap-1 pt-4">
                    <input type="checkbox" data-testid={`webhook-${k}-enabled`} checked={settings.discord?.enabled?.[k] ?? true}
                      onChange={(e) => setSettings({ ...settings, discord: { ...settings.discord, enabled: { ...settings.discord.enabled, [k]: e.target.checked } } })}
                      className="w-4 h-4 accent-[#138808]" />
                    <button onClick={() => testDiscord(k)} data-testid={`webhook-${k}-test`} className="text-[10px] font-bold text-[#003366] hover:underline">Test</button>
                  </div>
                </div>
              ))}
              <button onClick={() => save({ discord: settings.discord })} data-testid="discord-save" className="w-full py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Save Webhooks</button>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-bold text-slate-900">Discord Log</h2>
                <a href={`${API}/admin/export/discord_logs`} data-testid="export-discord-logs" className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-[#FF9933]"><Download className="w-3.5 h-3.5" /> CSV</a>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {discordLogs.logs.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                    <span className="truncate text-slate-600"><b>{l.event}</b> — {l.title}</span>
                    <span className={`font-bold shrink-0 ml-2 ${l.status === "sent" ? "text-[#138808]" : l.status === "failed" ? "text-red-600" : "text-slate-400"}`}>{l.status}</span>
                  </div>
                ))}
                {discordLogs.logs.length === 0 && <p className="text-sm text-slate-400">No Discord events logged yet.</p>}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
